import {FC, useEffect, useState, FocusEvent, useRef} from 'react';
import {toast} from 'react-toastify';
//mui
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
} from '@mui/material';
//store
import {getCurrentEventSelector, getEvent} from '@/store/organization-event';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {
  clearTicketsTiers,
  getTablesAndTicketsListSelector,
  getTablesAndTicketsStatisticsSelector,
  getTablesAndTicketsTiersSelector,
  getTicketsList,
  getTicketsTiers,
  updateTicketStatus,
} from '@/store/organization-tables';
//components
import Loader from '@/shared/components/Loader';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Button from '@/shared/components/Button';
import Input from '@/shared/components/Inputs/Input';
import Modal from '@/shared/components/Modal';
import AddTableTicket from '../AddTableTicket';
import TableDetails from '../TableDetails';
import AttendeeDetails from '@/components/Organization/Attendees/AttendeeDetails';
//types
import {IFilterGroup, IOrganizationTicketsListData, PaymentResult} from '@/utils/types';
//helpers
import {camelize, getErrorMessage, getTableStatusStyle} from '@/utils/helpers';
import {
  getUpdatedFilterData,
  handleSetTierIds,
  tableAndTicketsDefaultFilterValues,
  tableAndTicketsDefaultFilterData,
  tableTicketsMenuContent,
  updatedTableAndTicketsFilterOptions,
} from './helpers';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as EllipseIcon} from '@/assets/icons/ellipse-green.svg';
import {ReactComponent as PlusIcon} from '@/assets/icons/plus.svg';
//services
import {OrganizationTableAndTicketsService} from '@/utils/services';
//constants
import {
  ADD_TICKET,
  BODY1,
  CONTACT_PHONE,
  PRICE,
  PURCHASER,
  RIGHT,
  SEATS,
  TABLE,
  TABLE_PURCHASER,
  TICKETS_TIERS,
  defaultPage,
  PAY_STATUS as PAYMENT_STATUS_TITLE,
  defaultPageSize,
  ADD_TICKETS,
  NUMBER,
  ALL_TICKETS_TIERS,
  PAY_STATUS,
  TICKET_TYPE,
  TABLE_NAME,
  HASH,
} from '@/utils/constants';
//enums
import {MENU_OPTION, TABLE_STATUS} from '@/utils/enums/tables.enum';
//hooks
import {useSkipFirstRenderEffect} from '@/utils/hooks';
//styles
import {tablesAndTicketsTableStyle} from './TablesAndTicketsTable.styles';
import {ITeamRole, ROUTER_URL} from '@/utils/enums';
import {useNavigate} from 'react-router-dom';
import {currencyFormatter} from '@/utils/helpers/currency';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {orgAPI} from '@/core/api';
import {attendeesTableStyle} from '../../Attendees/AttendeesTable/AttendeesTable.styles';
import {getAttendee, getAttendeeSelector} from '@/store/organization-attendees';
import {attendeeDetailsTabs} from '../../Attendees/AttendeeDetails/helpers';
import {getTeamSelector} from '@/store/organization';
import {useApiInstance} from '@/contexts/ApiInstanceContext';

const TablesAndTicketsTable: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {apiInstance} = useApiInstance();

  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(defaultPage);
  const [searchName, setSearchName] = useState<string>('');

  const [filterData, setFilterData] = useState<IFilterGroup[]>(tableAndTicketsDefaultFilterData);
  const [filterValues, setFilterValues] = useState(tableAndTicketsDefaultFilterValues);

  const [isShowAddTicketModal, setIsShowAddTicketModal] = useState<boolean>(false);
  const [isShowTicketDetailsModal, setIsShowTicketDetailsModal] = useState<boolean>(false);
  const [isNoifyModalOpen, setIsNoifyModalOpen] = useState<boolean>(false);
  const [detailsTicketId, setDetailsTicketId] = useState<number>();
  const [isProfilePage, setIsProfilePage] = useState<boolean>(false);
  const [isShowAttendeeDetailsModal, setIsShowAttendeeDetailsModal] = useState<boolean>(false);
  const [activeAttendeeTab, setActiveAttendeeTab] = useState<string>('');
  const [isAccessModalOpen, setIsAccessModalOpen] = useState<boolean>(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const currentEvent = useAppSelector(getCurrentEventSelector);
  const ticketsList = useAppSelector(getTablesAndTicketsListSelector);
  const ticketsTiers = useAppSelector(getTablesAndTicketsTiersSelector);
  const tableStatistics = useAppSelector(getTablesAndTicketsStatisticsSelector);
  const attendee = useAppSelector(getAttendeeSelector);
  const team = useAppSelector(getTeamSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  const {data, pageSize, totalCount} = ticketsList;

  const isLaptop = useMediaQuery('(min-width:1200px)');

  useSkipFirstRenderEffect(() => {
    if (currentEvent) getTicketsListData(searchName);
  }, [currentPage, filterValues]);

  useEffect(() => {
    if (currentEvent?.tableInfos.length) {
      dispatch(getTicketsTiers({reqBody: currentEvent.id, apiInstance: orgAPI}));
    } else {
      dispatch(clearTicketsTiers());
    }
  }, [currentEvent?.id]);

  useEffect(() => {
    if (ticketsTiers) setFilterData(updatedTableAndTicketsFilterOptions(ticketsTiers, filterData));
  }, [ticketsTiers]);

  const handleNotifyModalChange = () => setIsNoifyModalOpen(!isNoifyModalOpen);
  const handleAccessModalChange = () => setIsAccessModalOpen(!isAccessModalOpen);

  const handleProfileTabStyles = (isProfilePage: boolean) => setIsProfilePage(isProfilePage);

  const handleAddTicketModalChange = () => {
    if (!currentEvent?.tableInfos.length) {
      setIsNoifyModalOpen(true);
      return;
    }

    if (isLaptop) {
      setIsShowAddTicketModal(!isShowAddTicketModal);
      getTicketsListData();
    } else navigate(ROUTER_URL.ORGANIZATION_PURCHASE_TICKETS);
  };

  const handleOpenTicketDetailsModal = (tableId?: number) => {
    setIsShowTicketDetailsModal(!isShowTicketDetailsModal);
    if (!isShowTicketDetailsModal && tableId) setDetailsTicketId(tableId);
    if (!tableId) getTicketsListData();
  };

  const getTicketsListData = async (value?: string) => {
    if (!currentEvent) return;
    setIsDataLoaded(false);

    try {
      const reqBody = {
        eventId: currentEvent!.id,
        page: isLaptop ? currentPage : 1,
        pageSize: isLaptop ? defaultPageSize : 10000,
        searchName: value,
        ...filterValues,
      };

      await dispatch(getTicketsList({reqBody, apiInstance: orgAPI})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDataLoaded(true);
    }
  };

  const setTableNumber = async (newNumber: string, tableId: number) => {
    try {
      const reqBody = {
        id: tableId,
        number: newNumber,
      };
      await OrganizationTableAndTicketsService.updateTableNumber(reqBody, apiInstance);
    } catch (error) {
      toast.error(getErrorMessage(error));
      getTicketsListData();
    }
  };

  const handleSortChange = (newSortColumn: string) => () => {
    const isAsc = filterValues.sortColumn !== newSortColumn || !filterValues.isOrderDesc;

    setFilterValues((prevState) => ({
      ...prevState,
      isOrderDesc: isAsc,
      sortColumn: newSortColumn,
    }));
  };

  const handleSearchChange = (value: string) => {
    setSearchName(value);
    getTicketsListData(value);
  };

  const handlePressEnter = () => {
    setCurrentPage(defaultPage);
    if (currentEvent) getTicketsListData();
  };

  const getPayStatusSx = (payStatus: string) => getTableStatusStyle(payStatus);

  const updateFilterData = (groupTitle: string, optionTitle: string, id?: number) => {
    if (optionTitle === ALL_TICKETS_TIERS && ticketsTiers) {
      return setFilterData(updatedTableAndTicketsFilterOptions(ticketsTiers, filterData));
    }

    return setFilterData((prevFilterData) => getUpdatedFilterData(prevFilterData, groupTitle, optionTitle, id));
  };

  const handleFilterChange = (groupTitle: string, optionTitle: string, id?: number) => {
    updateFilterData(groupTitle, optionTitle, id);

    switch (groupTitle) {
      case SEATS:
        setFilterValues((prevState) => ({
          ...prevState,
          status: camelize(optionTitle),
        }));
        break;

      case PAYMENT_STATUS_TITLE:
        setFilterValues((prevState) => ({
          ...prevState,
          isPaid: camelize(optionTitle),
        }));
        break;

      case TICKETS_TIERS:
        const newTicketTiersId = handleSetTierIds(filterValues.tierIds, id);
        setFilterValues({
          ...filterValues,
          tierIds: newTicketTiersId as null,
        });
        break;

      default:
        break;
    }
  };

  const handleChangeAttendeeDetailsModal = async (attendeeId?: number) => {
    if (!attendeeId) return;

    setIsShowAttendeeDetailsModal(!isShowAttendeeDetailsModal);

    if (!isShowAttendeeDetailsModal && attendeeId) {
      try {
        await dispatch(getAttendee(attendeeId)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const handleUpdateTableNumber = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>, tableId: number) => {
    const {value} = event.target;
    setTableNumber(value, tableId);
  };

  const handleDeleteTable = async (tableId: number, item: IOrganizationTicketsListData) => {
    if (!currentEvent || item.isPaid) return;
    try {
      await OrganizationTableAndTicketsService.deleteTableAndTickets(tableId);
      getTicketsListData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateStatus = async ({id, isPaid}: {id: number; isPaid: boolean}) => {
    if (!currentEvent || isPaid) return;
    if (isStandartMember) {
      handleAccessModalChange();
      return;
    }

    try {
      await dispatch(updateTicketStatus({tableId: id, isPaid: true})).unwrap();
      await getTicketsListData();
      await dispatch(getEvent(currentEvent?.id)).unwrap();
      toast.success('Payment status updated successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const sendTablePaymentLink = async (tableId: number, item: IOrganizationTicketsListData) => {
    if (!currentEvent || item.isPaid || !item.purchaserAttendeeId) return;

    try {
      await OrganizationStripeService.sendPaymentLink(item.purchaserAttendeeId, currentEvent?.id);
      toast.success('Payment link sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleClickMenuOption = (option: string, tableId: number, item: IOrganizationTicketsListData) => async () => {
    switch (option) {
      case MENU_OPTION.DELETE:
        await handleDeleteTable(tableId, item);

        break;

      case MENU_OPTION.DETAILS:
        isLaptop
          ? handleOpenTicketDetailsModal(tableId)
          : navigate(`${ROUTER_URL.ORGANIZATION_TABLES_DETAILS}/${tableId}`);
        break;

      case MENU_OPTION.MARK_AS_PAID:
        setDetailsTicketId(tableId);
        handleUpdateStatus({id: tableId, isPaid: item.isPaid});
        break;

      case MENU_OPTION.SEND_PAYMENT_LINK:
        sendTablePaymentLink(tableId, item);
        break;

      case MENU_OPTION.COLLECT_PAYMENT:
        if (item.isPaid) return;
        setActiveAttendeeTab(attendeeDetailsTabs[2]);
        isLaptop
          ? handleChangeAttendeeDetailsModal(item.purchaserAttendeeId)
          : navigate(`${ROUTER_URL.ORGANIZATION_ATTENDEES}/${item.purchaserAttendeeId}`, {
              state: {activeTab: attendeeDetailsTabs[2], isTabsVisible: false},
            });
        break;

      default:
        break;
    }
  };

  const handleNavigate = (id: number) => {
    isLaptop ? handleOpenTicketDetailsModal(id) : navigate(`${ROUTER_URL.ORGANIZATION_TABLES_DETAILS}/${id}`);
  };

  const MAX_RETRIES = 5;

  const checkPaymentStatus = async (paymentId: string, retryCount = 0, toastId?: string | number) => {
    if (retryCount === 0) {
      toastId = toast.loading('Checking payment status...');
    }

    try {
      const {data} = await OrganizationStripeService.checkPaymentStatus(+paymentId, orgAPI);

      if (data.status === PaymentResult.COMPLETED) {
        if (toastId) toast.dismiss(toastId);
        toast.success('Payment completed successfully!');
        getTicketsListData();
      } else if (data.status === PaymentResult.PENDING) {
        if (retryCount < MAX_RETRIES) {
          timeoutRef.current = setTimeout(() => {
            checkPaymentStatus(paymentId, retryCount + 1, toastId);
          }, 3000);
        } else {
          if (toastId) toast.dismiss(toastId);
          toast.info('Payment is still being processed. Please check the status in a few minutes.');
        }
      } else if (data.status === PaymentResult.FAILED) {
        if (toastId) toast.dismiss(toastId);
        toast.error('Payment failed!');
      } else if (data.status === PaymentResult.REFUNDED) {
        if (toastId) toast.dismiss(toastId);
        toast.error('Payment refunded!');
      }
    } catch (error) {
      if (toastId) toast.dismiss(toastId);
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchName, filterValues]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get('paymentId');

    if (paymentId) {
      checkPaymentStatus(paymentId);

      params.delete('paymentId');
      params.delete('session_id');
      params.delete('organizationId');

      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    return () => clearTimeout(timeoutRef.current);
  }, []);

  return (
    <Box sx={tablesAndTicketsTableStyle.wrapper}>
      <SearchHeader
        page={currentPage}
        items={totalCount < defaultPageSize ? totalCount : defaultPageSize}
        total={totalCount}
        onPressEnter={handlePressEnter}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
        filterOption={filterData}
        parentClassName={{padding: '0 16px'}}
        actionButton={
          <Button
            onClick={handleAddTicketModalChange}
            title={ADD_TICKET}
            startIcon={<PlusIcon />}
            sx={tablesAndTicketsTableStyle.searchButton}
            disabled={isStandartMember}
          />
        }
      />

      {isDataLoaded ? (
        totalCount ? (
          <Box sx={tablesAndTicketsTableStyle.tableContent}>
            <TableContainer sx={tablesAndTicketsTableStyle.container}>
              <Table sx={tablesAndTicketsTableStyle.table}>
                <TableHead>
                  <TableRow sx={tablesAndTicketsTableStyle.tableHeader}>
                    <TableCell>{TICKET_TYPE}</TableCell>

                    <TableCell>{TABLE_NAME}</TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(PURCHASER)} IconComponent={UpDownIcon}>
                        {TABLE_PURCHASER}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>{CONTACT_PHONE}</TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(PRICE)} IconComponent={UpDownIcon}>
                        {PRICE}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>{PAY_STATUS}</TableCell>

                    <TableCell align={RIGHT}>
                      <TableSortLabel active onClick={handleSortChange(NUMBER)} IconComponent={UpDownIcon}>
                        {TABLE}
                        {HASH}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.id} sx={tablesAndTicketsTableStyle.tableBody}>
                      <TableCell onClick={() => handleNavigate(row.id)}>{row.typeOfSeat}</TableCell>

                      <TableCell onClick={() => handleNavigate(row.id)}>{row.name}</TableCell>

                      <TableCell onClick={() => handleNavigate(row.id)}>
                        <Box>
                          {' '}
                          <Typography>{row.purchaser}</Typography>
                          <Typography
                            sx={{
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                            }}
                          >
                            {row.email}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell onClick={() => handleNavigate(row.id)}>{row.contactPhone}</TableCell>

                      <TableCell onClick={() => handleNavigate(row.id)}>
                        {currencyFormatter.format(row.price)}
                      </TableCell>

                      <TableCell onClick={() => handleNavigate(row.id)}>
                        <Typography sx={getPayStatusSx(row.isPaid ? TABLE_STATUS.PAID : TABLE_STATUS.PENDING)}>
                          {row.isPaid ? TABLE_STATUS.PAID : TABLE_STATUS.PENDING}
                          <EllipseIcon />
                        </Typography>
                      </TableCell>

                      <TableCell align={RIGHT}>
                        <Input
                          defaultValue={row.number}
                          onBlur={(event) => {
                            handleUpdateTableNumber(event, row.id);
                          }}
                          type={NUMBER}
                        />
                      </TableCell>

                      <TableCell>
                        <ActionsMenu
                          disableRipple
                          content={
                            <Box>
                              {tableTicketsMenuContent.map((item) => (
                                <Typography
                                  key={item}
                                  onClick={handleClickMenuOption(item, row.id, row)}
                                  variant={BODY1}
                                  sx={{
                                    ...tablesAndTicketsTableStyle.menuItem,
                                    ...(item === MENU_OPTION.MARK_AS_PAID &&
                                      row.isPaid &&
                                      tablesAndTicketsTableStyle.disabledMenuItem),
                                    ...(item === MENU_OPTION.SEND_PAYMENT_LINK &&
                                      row.isPaid &&
                                      tablesAndTicketsTableStyle.disabledMenuItem),
                                    ...(item === MENU_OPTION.COLLECT_PAYMENT &&
                                      row.isPaid &&
                                      tablesAndTicketsTableStyle.disabledMenuItem),
                                    ...(item === MENU_OPTION.DELETE &&
                                      row.isPaid &&
                                      tablesAndTicketsTableStyle.disabledMenuItem),
                                  }}
                                >
                                  {item}
                                </Typography>
                              ))}
                            </Box>
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={tablesAndTicketsTableStyle.pagination}>
              <Pagination
                page={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(step) => setCurrentPage(step)}
              />
            </Box>
          </Box>
        ) : (
          <EmptyTableData
            text={tableStatistics?.tablesSold ? 'No data found' : 'You do not have any tables or tickets'}
          />
        )
      ) : (
        <Loader parentClassName={tablesAndTicketsTableStyle.loader} size={50} />
      )}

      <Modal
        onClose={handleAddTicketModalChange}
        open={isShowAddTicketModal}
        sx={tablesAndTicketsTableStyle.modal}
        title={ADD_TICKETS}
      >
        <AddTableTicket onCloseModal={handleAddTicketModalChange} />
      </Modal>

      <Modal
        onClose={handleOpenTicketDetailsModal}
        open={isShowTicketDetailsModal}
        sx={tablesAndTicketsTableStyle.modal}
      >
        <TableDetails onCloseModal={handleOpenTicketDetailsModal} currentTableId={detailsTicketId} />
      </Modal>

      <Modal
        onClose={handleNotifyModalChange}
        open={isNoifyModalOpen}
        title="Action needed"
        description="Add a table tier before you can assign guests to a table."
        sx={tablesAndTicketsTableStyle.modalNotify}
      />
      <Modal
        onClose={handleChangeAttendeeDetailsModal}
        open={isShowAttendeeDetailsModal && !!attendee}
        sx={{...attendeesTableStyle.detailsModal, ...(isProfilePage && attendeesTableStyle.profilePage)}}
      >
        <AttendeeDetails
          onCloseModal={handleChangeAttendeeDetailsModal}
          handleProfileTabStyles={handleProfileTabStyles}
          activeAttendeeTab={activeAttendeeTab}
          isTabsVisible={false}
        />
      </Modal>
      <Modal
        open={isAccessModalOpen}
        onClose={handleAccessModalChange}
        title="Access Denied"
        description="You do not have access to this feature"
        sx={tablesAndTicketsTableStyle.modalNotify}
      />
    </Box>
  );
};

export default TablesAndTicketsTable;
